import React, { useMemo } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

const STATE_CENTERS = {
  AZ: { lat: 34.0489, lng: -111.0937, name: 'Arizona' },
  CA: { lat: 36.7783, lng: -119.4179, name: 'California' },
  FL: { lat: 27.9944, lng: -81.7603, name: 'Florida' },
  GA: { lat: 32.1656, lng: -83.5085, name: 'Georgia' },
  ID: { lat: 44.0682, lng: -114.742, name: 'Idaho' },
  NV: { lat: 38.8026, lng: -116.4194, name: 'Nevada' },
  OR: { lat: 43.8041, lng: -120.5542, name: 'Oregon' },
  WA: { lat: 47.7511, lng: -120.7401, name: 'Washington' },
  TX: { lat: 31.9686, lng: -99.9018, name: 'Texas' },
  NY: { lat: 42.1657, lng: -74.9481, name: 'New York' },
  IL: { lat: 40.6331, lng: -89.3985, name: 'Illinois' },
  OH: { lat: 40.4173, lng: -82.9071, name: 'Ohio' },
  PA: { lat: 41.2033, lng: -77.1945, name: 'Pennsylvania' },
  NC: { lat: 35.7596, lng: -79.0193, name: 'North Carolina' },
  SC: { lat: 33.8361, lng: -81.1637, name: 'South Carolina' },
  VA: { lat: 37.4316, lng: -78.6569, name: 'Virginia' },
  TN: { lat: 35.5175, lng: -86.5804, name: 'Tennessee' },
  AL: { lat: 32.3182, lng: -86.9023, name: 'Alabama' },
  CO: { lat: 39.5501, lng: -105.7821, name: 'Colorado' },
  UT: { lat: 39.321, lng: -111.0937, name: 'Utah' },
};

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '16px',
  },
  mapContainer: {
    width: '100%',
    height: '400px',
    borderRadius: '6px',
    overflow: 'hidden',
    border: '1px solid #e5e7eb',
  },
  popupTitle: {
    fontWeight: 600,
    fontSize: '14px',
    marginBottom: '6px',
    color: '#1f2937',
  },
  popupRow: {
    fontSize: '12px',
    color: '#4b5563',
    marginBottom: '2px',
  },
};

function interpolateGreenToRed(t) {
  // t: 0 = green (cheapest), 1 = red (most expensive)
  const r = Math.round(22 + (220 - 22) * t);
  const g = Math.round(163 + (38 - 163) * t);
  const b = Math.round(74 + (38 - 74) * t);
  return `rgb(${r}, ${g}, ${b})`;
}

export default function PriceMap({ geoData }) {
  const stateGroups = useMemo(() => {
    if (!geoData || geoData.length === 0) return [];

    const grouped = {};
    geoData.forEach((item) => {
      const st = (item.state || '').toUpperCase();
      if (!grouped[st]) {
        grouped[st] = { addresses: [], totalPest365: 0, count: 0 };
      }
      grouped[st].addresses.push(item);
      if (item.currentPest365 != null) {
        grouped[st].totalPest365 += item.currentPest365;
        grouped[st].count += 1;
      }
    });

    const states = Object.entries(grouped).map(([state, data]) => {
      const center = STATE_CENTERS[state];
      const avgPest365 = data.count > 0 ? data.totalPest365 / data.count : 0;
      return {
        state,
        stateName: center ? center.name : state,
        lat: center ? center.lat : null,
        lng: center ? center.lng : null,
        addressCount: data.addresses.length,
        avgPest365,
        addresses: data.addresses,
      };
    }).filter((s) => s.lat != null);

    // Calculate color normalization
    const avgValues = states.map((s) => s.avgPest365).filter((v) => v > 0);
    const minAvg = Math.min(...avgValues);
    const maxAvg = Math.max(...avgValues);
    const range = maxAvg - minAvg || 1;

    states.forEach((s) => {
      s.colorT = avgValues.length > 0 ? (s.avgPest365 - minAvg) / range : 0.5;
      s.color = interpolateGreenToRed(s.colorT);
    });

    return states;
  }, [geoData]);

  if (!geoData || geoData.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Geographic Price Distribution</div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No geographic data available
        </div>
      </div>
    );
  }

  const maxAddressCount = Math.max(...stateGroups.map((s) => s.addressCount), 1);

  return (
    <div style={styles.card}>
      <div style={styles.title}>Geographic Price Distribution</div>
      <div style={styles.mapContainer}>
        <MapContainer
          center={[39, -98]}
          zoom={4}
          style={{ width: '100%', height: '100%' }}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {stateGroups.map((sg) => {
            const radius = 10 + (sg.addressCount / maxAddressCount) * 25;
            return (
              <CircleMarker
                key={sg.state}
                center={[sg.lat, sg.lng]}
                radius={radius}
                pathOptions={{
                  fillColor: sg.color,
                  fillOpacity: 0.75,
                  color: '#fff',
                  weight: 2,
                }}
              >
                <Popup>
                  <div>
                    <div style={styles.popupTitle}>
                      {sg.stateName} ({sg.state})
                    </div>
                    <div style={styles.popupRow}>
                      Addresses: <strong>{sg.addressCount}</strong>
                    </div>
                    <div style={styles.popupRow}>
                      Avg Pest365: <strong>${sg.avgPest365.toFixed(2)}</strong>
                    </div>
                    {sg.addresses.slice(0, 5).map((a, i) => (
                      <div key={i} style={{ ...styles.popupRow, fontSize: '11px', color: '#9ca3af' }}>
                        {a.address || a.city} - ${(a.currentPest365 || 0).toFixed(2)}
                      </div>
                    ))}
                    {sg.addresses.length > 5 && (
                      <div style={{ ...styles.popupRow, fontSize: '11px', fontStyle: 'italic' }}>
                        +{sg.addresses.length - 5} more
                      </div>
                    )}
                  </div>
                </Popup>
              </CircleMarker>
            );
          })}
        </MapContainer>
      </div>
    </div>
  );
}
